import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import CreatePlan from "./CreatePlan";
import EditPlan from "./EditPlan";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddonForm from "./AddonForm";
import { BACKEND_BASE_URL } from "../../config";

const API_BASE_URL = `${BACKEND_BASE_URL}/api`;

const Plan = () => {

  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAddons, setLoadingAddons] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  
  // Confirmation dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'plan' or 'addon'
  
  const token = localStorage.getItem("authToken");

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/subscription-plans/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("Failed to fetch plans.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAddons = async () => {
    setLoadingAddons(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/addons/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAddons(response.data);
    } catch (error) {
      console.error("Error fetching addons:", error);
      toast.error("Failed to fetch addons.");
    } finally {
      setLoadingAddons(false);
    }
  };

  const fetchCurrencyOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/plan-types/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrencyOptions(response.data.currencies || []);
    } catch (error) {
      console.error("Error fetching currency options:", error);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchAddons();
    fetchCurrencyOptions();
  }, []);

  const handleAddClick = () => {
    setEditingPlan(null);
    setDialogOpen(true);
  };

  const handleEditClick = (plan) => {
    setEditingPlan(plan);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Open confirmation dialog
  const handleDeleteClick = (item, isAddon = false) => {
    setItemToDelete(item);
    setDeleteType(isAddon ? 'addon' : 'plan');
    setConfirmDialogOpen(true);
  };

  // Close confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setItemToDelete(null);
    setDeleteType('');
  };

  // Confirm delete action
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    const isAddon = deleteType === 'addon';
    try {
      await axios.delete(
        `${API_BASE_URL}/${isAddon ? "addons" : "subscription"}/${itemToDelete.id}/delete/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(`${isAddon ? "Addon" : "Plan"} deleted successfully!`);
      isAddon ? fetchAddons() : fetchPlans();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(`Failed to delete ${isAddon ? "addon" : "plan"}.`);
    } finally {
      handleCloseConfirmDialog();
    }
  };

  const handleSuccess = () => {
    fetchPlans();
    fetchAddons();
    handleCloseDialog();
  };

  // Helper function to format prices display
  const formatPricesDisplay = (prices, priceType = 'monthly') => {
    if (!prices || prices.length === 0) return "-";
    
    if (prices.length === 1) {
      const price = prices[0];
      const amount = priceType === 'monthly' ? price.monthly_price : price.yearly_price;
      return amount ? `${price.currency} ${amount}` : "-";
    }
    
    // Multiple currencies - show as chips
    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {prices.map((price, index) => {
          const amount = priceType === 'monthly' ? price.monthly_price : price.yearly_price;
          return amount ? (
            <Chip
              key={index}
              label={`${price.currency} ${amount}`}
              size="small"
              variant="outlined"
            />
          ) : null;
        }).filter(Boolean)}
      </Box>
    );
  };

  // Helper function to format addon prices display
  const formatAddonPricesDisplay = (prices) => {
    if (!prices || prices.length === 0) return "-";
    
    if (prices.length === 1) {
      const price = prices[0];
      return `${price.currency} ${price.price}`;
    }
    
    // Multiple currencies - show as chips
    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {prices.map((price, index) => (
          <Chip
            key={index}
            label={`${price.currency} ${price.price}`}
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    );
  };

  return (
    <Box p={3}>
      {/* ---------- Main Plans ---------- */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <Typography variant="h4" gutterBottom>
          Manage Membership Subscriptions
        </Typography>

        <Button variant="contained" onClick={handleAddClick}>
          Add Membership Plan
        </Button>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#152F8F' }}>
              <TableRow>
                <TableCell sx={{color:'white'}}><strong>Plan Name</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Monthly Price</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Yearly Price</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Allowed Towns</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Explain Limit</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Ask Limit</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {plan.name}
                      {plan.is_most_popular && (
                        <Chip label="Most Popular" color="primary" size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {formatPricesDisplay(plan.prices, 'monthly')}
                  </TableCell>
                  <TableCell>
                    {formatPricesDisplay(plan.prices, 'yearly')}
                  </TableCell>
                  <TableCell>{plan.unlimited_towns ? 'Unlimited' : plan.allowed_towns}</TableCell>
                  <TableCell>{plan.aware_explain_limit}</TableCell>
                  <TableCell>{plan.aware_ask_limit}</TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => handleEditClick(plan)} sx={{ mr: 1 }}>
                      Edit
                    </Button>
                    <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteClick(plan, false)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ---------- Addons Section ---------- */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "40px" }}>
        <Typography variant="h5" gutterBottom>
          Manage Add-on Subscriptions
        </Typography>

        <Button variant="contained" onClick={() => { setEditingPlan({ type: "addon" }); setDialogOpen(true); }}>
          Add Add-on
        </Button>
      </div>

      {loadingAddons ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : addons.length === 0 ? (
        <Typography>No add-on subscriptions available.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#152F8F' }}>
              <TableRow>
                <TableCell sx={{color:'white'}}><strong>Add-on Name</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Feature Type</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Credits</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Prices</strong></TableCell>
                <TableCell sx={{color:'white'}}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {addons.map((addon) => (
                <TableRow key={addon.id}>
                  <TableCell>{addon.name}</TableCell>
                  <TableCell>
                    {addon.feature_type === "explain" ? "Explain Credits" : "Ask Aware Credits"}
                  </TableCell>
                  <TableCell>{addon.credits}</TableCell>
                  <TableCell>
                    {formatAddonPricesDisplay(addon.prices)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => { setEditingPlan({ ...addon, type: "addon" }); setDialogOpen(true); }}
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick(addon, true)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ---------- Main Dialog (Create/Edit) ---------- */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPlan?.type === "addon"
            ? editingPlan.id
              ? "Edit Add-on"
              : "Add Add-on"
            : editingPlan
              ? "Edit Plan"
              : "Add Plan"}
        </DialogTitle>
        <DialogContent>
          {editingPlan?.type === "addon" ? (
            <AddonForm
              addonData={editingPlan.id ? editingPlan : null}
              onSuccess={handleSuccess}
              onCancel={handleCloseDialog}
            />
          ) : editingPlan ? (
            <EditPlan 
              planData={editingPlan} 
              onSuccess={handleSuccess} 
              onCancel={handleCloseDialog}
              currencyOptions={currencyOptions}
            />
          ) : (
            <CreatePlan 
              onSuccess={handleSuccess} 
              onCancel={handleCloseDialog}
              currencyOptions={currencyOptions}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- Confirmation Dialog ---------- */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to delete {deleteType === 'addon' ? 'this add-on' : 'this plan'}? 
            {itemToDelete && (
              <strong> "{itemToDelete.name}"</strong>
            )}
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast container */}
      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default Plan;
